import { Box, Modal } from "@mui/material";
import { StatusItem } from "@types";
import { useEffect, useState } from "react";
import { usePopupWindow } from "../../../hooks/usePopupWindow";
import StatusContent from "./StatusContent";
import { SF } from "../../../utils/utils";
import { useStatusContext } from "../../../context/InspStatusContext";
import { getInspStatus } from "../../../services/inspectService";
import { toast } from "react-toastify";
import { checkModuleAndImport } from "common-lib";
import MakeDataSetContent from "./MakeDataSetContent";
import { getInspModel } from "../../../services/inspModelService";
import { t } from "i18next";
import { toQuery } from "../../../utils/utils";
import StatusModalBody from "./StatusModalBody";

const PAGE_SIZE = 20;

interface ModalProps {
  open: boolean;
  onClose: () => void;
}

export default function StatusModal({ open, onClose }: ModalProps) {
  const { openPopup, renderInPopup, isOpen, popupWindow } = usePopupWindow();
  const { selectedStatus, setSelectedStatus, page, setPage, imageList, setImageList, filters, totalDataCount } =
    useStatusContext();
  const currentIndex = imageList?.findIndex((item) => item.id === selectedStatus!.id) ?? -1;
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [importedModel, setImportedModel] = useState(null);
  const [openMakeDataSet, setOpenMakeDataSet] = useState(false);
  const [reviewOptions, setReviewOptions] = useState<string[]>(["OK", "NG"]);
  const [form, setForm] = useState<StatusItem>({
    id: -1,
    uuid: "",
    name: "",
    client_ip: "",
    material_name: "",
    lot: "",
    zone: "",
    x: 0,
    y: 0,
    label: "",
    score: 0,
    result: "",
    review: "",
    path: "",
    create_at: "",
    model_uuid: "",
  });

  useEffect(() => {
    if (selectedStatus && selectedStatus.model_uuid) {
      fetchModule();
    }
  }, [selectedStatus]);

  const fetchModule = async () => {
    const inspModelInfo = await getInspModel(selectedStatus?.model_uuid);
    const ngList: string[] = inspModelInfo.data?.options?.AI_Vision?.convertlabelOKNG?.para?.NG_list ?? [];
    setReviewOptions(Array.from(new Set(["OK", "NG", ...ngList])));
    try {
      const importResult = await checkModuleAndImport(inspModelInfo.data, selectedStatus?.client_ip);
      setImportedModel(importResult.data);
    } catch (error) {
      console.error("checkModuleAndImport failed:", error);
      setImportedModel(null);
      toast.error(t("ModelImportFailMsg"));
    }
  };

  const moveToOuterPopup = () => {
    openPopup("width=1400,height=900", () => {
      onClose();
    });
  };

  const handlePrevious = async () => {
    if (!imageList) return;

    if (currentIndex > 0) {
      const prevStatus = imageList[currentIndex - 1];
      setSelectedStatus(prevStatus);
      setForm({ ...prevStatus });
      return;
    }

    if (page <= 1) {
      toast(t("FirstPageMsg"));
      return;
    }

    try {
      const prevPage = page - 1;
      const response = await getInspStatus({ page: prevPage, ...toQuery(filters) });
      const prevContents = response.data.contents;

      if (!prevContents || prevContents.length === 0) {
        toast(t("NoDataToLoadMsg"));
        return;
      }

      setPage(prevPage);
      setImageList(prevContents);

      const prevStatus = prevContents[prevContents.length - 1];
      setSelectedStatus(prevStatus);
      setForm({ ...prevStatus });
    } catch (error) {
      console.log(error);
    }
  };

  const handleNext = async () => {
    if (!imageList) return;

    if (currentIndex < imageList.length - 1) {
      const nextStatus = imageList[currentIndex + 1];
      setSelectedStatus(nextStatus);
      setForm({ ...nextStatus });
      return;
    }

    try {
      const nextPage = page + 1;
      const response = await getInspStatus({ page: nextPage, ...toQuery(filters) });
      const nextContents = response.data.contents;

      if (!nextContents || nextContents.length === 0) {
        toast(t("LastPageMsg"));
        return;
      }

      setPage(nextPage);
      setImageList(nextContents);

      const nextStatus = nextContents[0];
      setSelectedStatus(nextStatus);
      setForm({ ...nextStatus });
    } catch (error) {
      console.log(error);
    }
  };

  // 리뷰 잔디에서 칸을 누르면 그 데이터로 이동한다.
  // 잔디는 id 오름차순, 목록은 내림차순이라 인덱스를 뒤집어 페이지를 구한다.
  // 조회 사이에 데이터가 쌓이면 뒤 페이지로 한 칸 밀리므로 id로 확인하고 다음 페이지까지 본다.
  const handleJump = async (id: number, ascIndex: number, total: number) => {
    const targetPage = Math.floor(Math.max(0, total - 1 - ascIndex) / PAGE_SIZE) + 1;

    for (const p of [targetPage, targetPage + 1]) {
      const response = await getInspStatus({ page: p, ...toQuery(filters) });
      const contents: StatusItem[] = response?.data?.contents ?? [];
      const hit = contents.find((c) => c.id === id);
      if (!hit) continue;

      setPage(p);
      setImageList(contents);
      setSelectedStatus(hit);
      setForm({ ...hit });
      return;
    }
    toast(t("NoDataToLoadMsg"));
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;

      // 목록(최신이 위) 기준: ↑ 최신 / ↓ 과거
      if (e.key === "ArrowUp") {
        e.preventDefault();
        handlePrevious();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        handleNext();
      }
      // 리뷰 잔디(왼쪽이 과거) 기준: ← 과거 / → 최신
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleNext();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        handlePrevious();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    popupWindow?.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      popupWindow?.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePrevious, handleNext, popupWindow]);

  return (
    <>
      {!isOpen && (
        <Modal
          id="status-modal"
          open={open}
          onClose={onClose}
          // transform으로 가운데 정렬하면 히트맵 렌즈(position: fixed)의 기준이 모달로 바뀌어 커서에서 어긋난다
          sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <Box>
            <StatusModalBody
              variant="modal"
              onClose={onClose}
              onPopup={moveToOuterPopup}
              openMakeDataSet={openMakeDataSet}
              setOpenMakeDataSet={setOpenMakeDataSet}
              form={form}
              setForm={setForm}
              imageSrc={imageSrc}
              setImageSrc={setImageSrc}
              onPrevious={handlePrevious}
              onNext={handleNext}
              onJump={handleJump}
              filters={filters}
              totalDataCount={totalDataCount}
              importedModel={importedModel}
              reviewOptions={reviewOptions}
            />
          </Box>
        </Modal>
      )}
      {renderInPopup(
        <StatusModalBody
          variant="popup"
          onClose={onClose}
          openMakeDataSet={openMakeDataSet}
          setOpenMakeDataSet={setOpenMakeDataSet}
          form={form}
          setForm={setForm}
          imageSrc={imageSrc}
          setImageSrc={setImageSrc}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onJump={handleJump}
          filters={filters}
          totalDataCount={totalDataCount}
          importedModel={importedModel}
          reviewOptions={reviewOptions}
        />,
      )}
    </>
  );
}
